import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAccounts } from '../api/getAccounts';
import { mapAccount, AccountView } from '../lib/mapAccount';
import { deriveRows, AccountsTab } from '../lib/deriveRows';

export const accountsKeys = { list: ['accounts'] as const };

interface UseAccountsParams {
  tab: AccountsTab;
  search: string;
  showInactive: boolean;
  sortAsc: boolean;
}

export const useAccounts = ({
  tab,
  search,
  showInactive,
  sortAsc,
}: UseAccountsParams) => {
  const query = useQuery({
    queryKey: accountsKeys.list,
    queryFn: getAccounts,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const all: AccountView[] = useMemo(
    () => (query.data?.accounts ?? []).map(mapAccount),
    [query.data]
  );

  const companies = useMemo(
    () => all.filter((a) => a.type === 'company'),
    [all]
  );
  const projects = useMemo(
    () => all.filter((a) => a.type === 'project'),
    [all]
  );

  const rows = useMemo(
    () => deriveRows(all, { tab, search, showInactive, sortAsc }),
    [all, tab, search, showInactive, sortAsc]
  );

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    scope: query.data?.scope ?? 'assigned',
    rows,
    companiesCount: companies.length,
    projectsCount: projects.length,
    activeCompaniesCount: companies.filter((a) => a.active).length,
    activeProjectsCount: projects.filter((a) => a.active).length,
  };
};
